import { useRef, useEffect } from "react";
import { getItem as safeGetItem } from "../utils/safeStorage";

/**
 * Plays a woosh + hotel bell chime using the Web Audio API.
 * No external sound file needed — all tones are generated in the browser.
 */
function playChime() {
  try {
    const c = new (window.AudioContext || window.webkitAudioContext)();
    const t = c.currentTime;

    // --- Layer A: Low rumble sweep ---
    const buf1 = c.createBuffer(1, c.sampleRate * 2, c.sampleRate);
    const ch1 = buf1.getChannelData(0);
    for (let i = 0; i < ch1.length; i++) ch1[i] = Math.random() * 2 - 1;
    const lowNoise = c.createBufferSource();
    lowNoise.buffer = buf1;
    const lowFilter = c.createBiquadFilter();
    lowFilter.type = "lowpass";
    lowFilter.frequency.setValueAtTime(200, t);
    lowFilter.frequency.linearRampToValueAtTime(600, t + 1.0);
    lowFilter.frequency.linearRampToValueAtTime(300, t + 1.67);
    const lowGain = c.createGain();
    lowGain.gain.setValueAtTime(0, t);
    lowGain.gain.linearRampToValueAtTime(0.08, t + 0.53);
    lowGain.gain.linearRampToValueAtTime(0.10, t + 1.0);
    lowGain.gain.exponentialRampToValueAtTime(0.001, t + 1.73);
    lowNoise.connect(lowFilter);
    lowFilter.connect(lowGain);
    lowGain.connect(c.destination);
    lowNoise.start(t);
    lowNoise.stop(t + 1.87);

    // --- Layer B: Mid-range breathy sweep ---
    const buf2 = c.createBuffer(1, c.sampleRate * 2, c.sampleRate);
    const ch2 = buf2.getChannelData(0);
    for (let i = 0; i < ch2.length; i++) ch2[i] = Math.random() * 2 - 1;
    const midNoise = c.createBufferSource();
    midNoise.buffer = buf2;
    const midFilter = c.createBiquadFilter();
    midFilter.type = "bandpass";
    midFilter.Q.value = 2;
    midFilter.frequency.setValueAtTime(300, t);
    midFilter.frequency.exponentialRampToValueAtTime(800, t + 0.67);
    midFilter.frequency.exponentialRampToValueAtTime(2000, t + 1.2);
    midFilter.frequency.exponentialRampToValueAtTime(600, t + 1.67);
    const midGain = c.createGain();
    midGain.gain.setValueAtTime(0, t);
    midGain.gain.linearRampToValueAtTime(0.06, t + 0.33);
    midGain.gain.linearRampToValueAtTime(0.13, t + 0.93);
    midGain.gain.linearRampToValueAtTime(0.08, t + 1.33);
    midGain.gain.exponentialRampToValueAtTime(0.001, t + 1.8);
    midNoise.connect(midFilter);
    midFilter.connect(midGain);
    midGain.connect(c.destination);
    midNoise.start(t);
    midNoise.stop(t + 1.87);

    // --- Layer C: High shimmer ---
    const buf3 = c.createBuffer(1, c.sampleRate * 2, c.sampleRate);
    const ch3 = buf3.getChannelData(0);
    for (let i = 0; i < ch3.length; i++) ch3[i] = Math.random() * 2 - 1;
    const hiNoise = c.createBufferSource();
    hiNoise.buffer = buf3;
    const hiFilter = c.createBiquadFilter();
    hiFilter.type = "highpass";
    hiFilter.frequency.setValueAtTime(3000, t);
    hiFilter.frequency.linearRampToValueAtTime(5000, t + 1.0);
    hiFilter.frequency.linearRampToValueAtTime(3000, t + 1.67);
    const hiGain = c.createGain();
    hiGain.gain.setValueAtTime(0, t);
    hiGain.gain.linearRampToValueAtTime(0.03, t + 0.67);
    hiGain.gain.linearRampToValueAtTime(0.05, t + 1.07);
    hiGain.gain.exponentialRampToValueAtTime(0.001, t + 1.67);
    hiNoise.connect(hiFilter);
    hiFilter.connect(hiGain);
    hiGain.connect(c.destination);
    hiNoise.start(t);
    hiNoise.stop(t + 1.87);

    // --- Hotel desk bell "ding" ---
    const bellTime = t + 1.2;
    const bellFreqs = [1480, 2960, 4440];
    const bellVols = [0.16, 0.07, 0.03];
    const bellDecays = [2.0, 1.2, 0.6];
    bellFreqs.forEach((freq, i) => {
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.connect(gain);
      gain.connect(c.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, bellTime);
      gain.gain.setValueAtTime(0, bellTime);
      gain.gain.linearRampToValueAtTime(bellVols[i], bellTime + 0.003);
      gain.gain.exponentialRampToValueAtTime(0.001, bellTime + bellDecays[i]);
      osc.start(bellTime);
      osc.stop(bellTime + bellDecays[i] + 0.1);
    });

    // Bell strike tap
    const tap = c.createOscillator();
    const tapGain = c.createGain();
    tap.connect(tapGain);
    tapGain.connect(c.destination);
    tap.type = "square";
    tap.frequency.setValueAtTime(4000, bellTime);
    tap.frequency.exponentialRampToValueAtTime(500, bellTime + 0.02);
    tapGain.gain.setValueAtTime(0.08, bellTime);
    tapGain.gain.exponentialRampToValueAtTime(0.001, bellTime + 0.05);
    tap.start(bellTime);
    tap.stop(bellTime + 0.06);
  } catch {
    // Audio not available — fail silently
  }
}

/**
 * Custom hook that triggers sound and browser notifications
 * when the unread message count goes up (i.e. new mail arrived).
 *
 * @param {number} unreadCount - current unread message count
 */
export default function useMessageNotifications(unreadCount) {
  const prevCountRef = useRef(unreadCount);

  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = unreadCount;

    // Only fire when count goes UP (new mail), not on initial load
    if (prev === undefined || prev === null || unreadCount <= prev) return;

    // Sound
    const soundEnabled =
      safeGetItem("dupont_message_sound") !== "false";
    if (soundEnabled) {
      playChime();
    }

    // Browser notification
    const notificationsEnabled =
      safeGetItem("dupont_browser_notifications") === "true";
    if (
      notificationsEnabled &&
      "Notification" in window &&
      Notification.permission === "granted"
    ) {
      new Notification("Du Pont Circle", {
        body: "You have a new letter!",
      });
    }
  }, [unreadCount]);
}
